import React, { useState, useEffect } from "react";

/**
 * Sticky table-of-contents nav for guide pages. Scans the rendered
 * article content for h2/h3 headings, assigns anchor IDs, and tracks
 * scroll position to highlight the active section.
 */
export default function GuideToc({ contentRef }) {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    if (!contentRef.current) return;
    const els = contentRef.current.querySelectorAll("h2, h3");
    const items = [];
    els.forEach((el, i) => {
      const id = el.id || `guide-heading-${i}`;
      el.id = id;
      el.style.scrollMarginTop = "100px";
      items.push({
        id,
        text: el.textContent.trim(),
        level: el.tagName.toLowerCase(),
      });
    });
    setHeadings(items);
  }, [contentRef]);

  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-80px 0px -70% 0px" }
    );
    headings.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <nav className="sticky top-32 max-h-[calc(100vh-9rem)] overflow-y-auto pb-8">
      <p className="font-mono text-[10px] uppercase tracking-widest text-steel mb-3">
        Contents
      </p>
      <ul className="space-y-1 border-l border-border">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              onClick={(e) => handleClick(e, h.id)}
              className={`block text-sm transition-colors border-l-2 -ml-[1px] ${
                h.level === "h3" ? "pl-5" : "pl-3"
              } ${
                activeId === h.id
                  ? "border-cobalt text-cobalt font-medium"
                  : "border-transparent text-steel hover:text-bone"
              }`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}